import { useCallback, useEffect, useState } from "react";
import { getQaAuto, setQaAuto, listQaBranches, type QaAutoConfig } from "../api/qaAuto";

type Props = { project: string; isAdmin: boolean };

// Admin-only per-pack "Automatic" (continuous QA) control. Arms/disarms the
// daemon-side scheduler for one pack and lets the admin pick which branch the
// loop tests AND how many runs/day it may do before pausing until midnight
// (Europe/Prague). Renders nothing for non-admins. Soft-degrades: read failures
// keep the last-known config; the daemon enforces single-armed + all gating.
export default function QaAutoControl({ project, isAdmin }: Props) {
  const [cfg, setCfg] = useState<QaAutoConfig | null>(null);
  const [branches, setBranches] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [maxRuns, setMaxRuns] = useState<string>(""); // local draft for the number input

  const adopt = useCallback((c: QaAutoConfig | null) => {
    setCfg(c);
    if (c) setMaxRuns(String(c.max_runs_per_day));
  }, []);

  const refresh = useCallback(async () => {
    try { const r = await getQaAuto(project); adopt(r.config); } catch { /* keep last-known */ }
  }, [project, adopt]);

  useEffect(() => {
    if (!isAdmin) return;
    void refresh();
    void listQaBranches(project).then(setBranches).catch(() => setBranches([]));
  }, [isAdmin, project, refresh]);

  const armed = cfg?.enabled === 1;

  // Single seam for every config mutation (branch / max-runs / arm) — add the
  // next knob (e.g. cooldown) by calling patch({ cooldownSeconds }) and nothing
  // else changes.
  const patch = useCallback(async (p: Parameters<typeof setQaAuto>[1]) => {
    setBusy(true);
    try { const r = await setQaAuto(project, p); adopt(r.config); }
    finally { setBusy(false); }
  }, [project, adopt]);

  const commitMaxRuns = useCallback(() => {
    const n = Math.trunc(Number(maxRuns));
    if (!Number.isFinite(n) || n < 1) { if (cfg) setMaxRuns(String(cfg.max_runs_per_day)); return; }
    if (!cfg || n === cfg.max_runs_per_day) return;
    void patch({ maxRunsPerDay: n });
  }, [maxRuns, cfg, patch]);

  if (!isAdmin) return null;

  const status = !armed ? "Automatic: Off"
    : cfg?.paused_reason === "cap" ? `Automatic: paused (daily cap ${cfg.runs_today}/${cfg.max_runs_per_day})`
    : cfg?.paused_reason === "circuit_breaker" ? "Automatic: paused (errors)"
    : cfg?.paused_reason === "global_off" ? "Automatic: paused (QA globally off)"
    : cfg?.in_flight_job_id != null ? `Automatic: ON · testing ${cfg.branch ?? "default"} · run ${cfg.runs_today}/${cfg.max_runs_per_day}`
    : `Automatic: ON · ${cfg?.branch ?? "default"} · run ${cfg?.runs_today}/${cfg?.max_runs_per_day}`;

  return (
    <div className={`qa-auto ${armed ? "qa-auto-on" : "qa-auto-off"}`}>
      <span className="qa-auto-pill">{status}</span>
      <select
        className="qa-auto-branch"
        value={cfg?.branch ?? ""}
        disabled={busy}
        onChange={(e) => void patch({ branch: e.target.value })}
        aria-label="Branch to test"
      >
        {(branches.length ? branches : [cfg?.branch ?? "main"]).map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
      </select>
      <label className="qa-auto-num" title="Max QA runs per day before the loop pauses until midnight (Europe/Prague)">
        max/day
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          size={3}
          value={maxRuns}
          disabled={busy}
          onChange={(e) => setMaxRuns(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={commitMaxRuns}
          onKeyDown={(e) => { if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur(); }}
          aria-label="Max runs per day"
        />
      </label>
      <button
        type="button"
        className="qa-auto-toggle"
        onClick={() => void patch({ enabled: !armed })}
        disabled={busy}
        aria-pressed={armed}
        aria-label="Toggle Automatic QA"
      >
        {armed ? "Stop Automatic" : "Start Automatic"}
      </button>
    </div>
  );
}
